/** GL-SPEC-APP-002 — Compliance Threshold Engine UI helpers. */
import { WarningCircleIcon, ShieldCheckIcon } from '@phosphor-icons/react';

export const INDUSTRY_OPTIONS = [
  { key: 'manufacturing', label: 'Manufacturing / Fabrication' },
  { key: 'warehouse', label: 'Warehouse / Distribution' },
  { key: 'construction', label: 'Construction / Contractor' },
  { key: 'fleet', label: 'Fleet / Transportation' },
  { key: 'general', label: 'General Industry / Other' },
];

/** Compute exemption flags from employee_count + industry_type. Mirrors backend. */
export function computeExemptions(employeeCount, industryType) {
  const n = Number(employeeCount) || 0;
  const flags = [];
  if (n < 1) return flags;

  if (n <= 10) {
    flags.push({
      key: 'eap_oral_allowed',
      title: 'EAP Exemption Active',
      description: `Facility has ${n} employee${n === 1 ? '' : 's'}. A written Emergency Action Plan is not required under 29 CFR 1910.38. Oral communication is acceptable. This has been noted in your report.`,
      cfr: '29 CFR 1910.38',
      severity: 'exempt',
    });
    flags.push({
      key: 'recordkeeping_partial_exempt',
      title: 'Recordkeeping Partial Exemption',
      description: `With ${n} employees, the facility qualifies for the partial 29 CFR Part 1904 recordkeeping exemption. The OSHA 300 Log is not required. Reporting of fatalities and severe injuries (1904.39) is still required.`,
      cfr: '29 CFR Part 1904',
      severity: 'exempt',
    });
  }
  if (n >= 250) {
    flags.push({
      key: 'ita_electronic_submission_required',
      title: 'OSHA ITA Electronic Submission Required',
      description: `Facility headcount is ${n}. Electronic submission of Form 300A to OSHA's Injury Tracking Application (ITA) is required annually by March 2 of the following year.`,
      cfr: '29 CFR 1904.41',
      severity: 'required',
    });
  }
  if (industryType === 'manufacturing') {
    flags.push({
      key: 'manufacturing_full_compliance',
      title: 'High-Hazard Classification',
      description: 'Manufacturing / Fabrication facilities receive no industry exemption from OSHA standards. All applicable General Industry standards (29 CFR 1910) apply in full regardless of headcount.',
      cfr: '29 CFR 1910',
      severity: 'required',
    });
  }
  return flags;
}

export function industryLabel(key) {
  return INDUSTRY_OPTIONS.find((o) => o.key === key)?.label || '';
}

/** Persistent amber banner — renders all active exemption flags. */
export function ExemptionBanner({ exemptions }) {
  if (!exemptions || exemptions.length === 0) return null;
  return (
    <div
      className="mx-5 md:mx-8 mb-3 border-l-4 rounded-sm"
      style={{ borderColor: 'var(--gl-gold)', background: '#fdf6e3' }}
      data-testid="exemption-banner"
    >
      <div className="px-4 py-3 space-y-3">
        {exemptions.map((ex) => {
          const Icon = ex.severity === 'required' ? WarningCircleIcon : ShieldCheckIcon;
          return (
            <div key={ex.key} className="flex items-start gap-3" data-testid={`exemption-${ex.key}`}>
              <Icon
                size={22}
                weight="fill"
                style={{ color: ex.severity === 'required' ? 'var(--gl-p2)' : 'var(--gl-navy)', flexShrink: 0, marginTop: 2 }}
              />
              <div className="flex-1">
                <div
                  className="font-display font-extrabold uppercase tracking-wider text-sm"
                  style={{ color: 'var(--gl-dark)', letterSpacing: '0.06em', fontFamily: 'Inter' }}
                >
                  {ex.title}
                </div>
                <div className="text-sm mt-0.5" style={{ color: 'var(--gl-dark)' }}>
                  {ex.description}
                </div>
                <div
                  className="text-xs mt-1 font-bold uppercase"
                  style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
                >
                  Reference · {ex.cfr}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Standalone employee count + industry inputs — reused on New + Setup. */
export function ThresholdFields({ employeeCount, industryType, onChange, required = false }) {
  return (
    <>
      <div>
        <div className="gl-label">
          How many employees work at this facility?{required && ' *'}
        </div>
        <input
          type="number"
          min={1}
          className="gl-input"
          value={employeeCount ?? ''}
          onChange={(e) => onChange('employee_count', e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1))}
          placeholder="e.g. 12"
          required={required}
          data-testid="eng-employee-count"
        />
        <div className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>
          Drives EAP, recordkeeping, and ITA submission thresholds. Integer ≥ 1.
        </div>
      </div>
      <div>
        <div className="gl-label">
          What best describes this facility?{required && ' *'}
        </div>
        <select
          className="gl-input"
          value={industryType || ''}
          onChange={(e) => onChange('industry_type', e.target.value)}
          required={required}
          data-testid="eng-industry-type"
        >
          <option value="">Select industry type…</option>
          {INDUSTRY_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <div className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>
          Manufacturing / Fabrication is high-hazard — no industry exemptions apply.
        </div>
      </div>
    </>
  );
}
